import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Check, ChevronDown } from 'lucide-react';

import Text from 'components/text';
import api from 'utils/api';
import { WorkPreference } from 'constants/collab-club';
import type { Artist } from 'types';

import { FONT } from '../constants';
import { onboardingPrefill, buildProfilePatch } from '../onboarding-prefill';
import ArtistIdentity from './artist-identity';
import OnboardingFields from './onboarding-fields';
import SecondaryButton from 'components/admin/secondary-button';

// Imperative handle so a parent form can persist this card's edits as part of its own submit
// (auto-save), in addition to the card's manual "Save profile" button.
export interface ArtistProfileCardHandle {
  save: () => Promise<void>;
}

interface ArtistProfileCardProps {
  artist: Artist;
  // Called after a successful save so the parent can refetch the roster and refresh the
  // displayed artist details (name / meta line).
  onSaved?: () => void;
}

// Per-artist onboarding card: edit the artist's onboarding answers and persist them either on
// the card's own "Save profile" button or automatically when the parent form submits (via the
// exposed `save` handle). Seeds local state from the artist's existing profile each time the
// artist changes.
const ArtistProfileCard = forwardRef<ArtistProfileCardHandle, ArtistProfileCardProps>(
  ({ artist, onSaved }, ref) => {
    const [location, setLocation] = useState<string | null>(null);
    const [genres, setGenres] = useState<string[]>([]);
    const [skills, setSkills] = useState<string[]>([]);
    const [workPreference, setWorkPreference] = useState<WorkPreference | ''>('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    // The onboarding fields are collapsed behind an "Edit profile" trigger by default.
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
      const p = onboardingPrefill(artist);
      setLocation(p.location);
      setGenres(p.genres);
      setSkills(p.skills);
      setWorkPreference(p.workPreference);
      setSaved(false);
      setErrorMsg(null);
      setExpanded(false);
      // Key on the artist id only: refreshing the same artist's data after a save must not
      // wipe the user's edits or collapse the card.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artist.id]);

    // Persist the current edits. No-op when nothing was filled in. Rethrows on failure so a
    // parent auto-save can abort its own submit; the manual button swallows the rejection.
    const runSave = async () => {
      const patch = buildProfilePatch({ location, genres, skills, workPreference });
      if (!Object.keys(patch).length) return;
      setSaving(true);
      setErrorMsg(null);
      try {
        await api.collabClub.updateAdminArtist({ id: artist.id, data: patch });
        setSaved(true);
        setExpanded(false);
        onSaved?.();
      } catch (err: any) {
        setErrorMsg(
          err?.data?.message || err?.message || 'Failed to save profile. Please try again.'
        );
        // Reveal the fields so the error (and the values that failed) are visible.
        setExpanded(true);
        throw err;
      } finally {
        setSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({ save: runSave }));

    const handleSaveClick = () => {
      runSave().catch(() => {});
    };

    return (
      <Box
        border="1px solid #e2e8f0"
        borderRadius="8px"
        padding="14px"
        display="flex"
        flexDirection="column"
        gap={2}
      >
        {/* Header: ArtistIdentity (same format as the Requests list) + Edit profile trigger */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <ArtistIdentity artist={artist} />
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            display="inline-flex"
            alignItems="center"
            gap="4px"
            sx={{
              flexShrink: 0,
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: '4px 6px',
              fontFamily: 'inherit',
              fontSize: FONT.sm,
              fontWeight: 500,
              color: '#475569',
            }}
          >
            Edit profile
            <ChevronDown
              size={14}
              style={{
                transition: 'transform .15s',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
            />
          </Box>
        </Box>

        {expanded && (
          <>
            <OnboardingFields
              location={location}
              genres={genres}
              skills={skills}
              workPreference={workPreference}
              onLocationChange={(v) => {
                setLocation(v);
                setSaved(false);
              }}
              onGenresChange={(v) => {
                setGenres(v);
                setSaved(false);
              }}
              onSkillsChange={(v) => {
                setSkills(v);
                setSaved(false);
              }}
              onWorkPreferenceChange={(v) => {
                setWorkPreference(v);
                setSaved(false);
              }}
              preferenceAction={
                <SecondaryButton
                  variant="outlined"
                  onClick={handleSaveClick}
                  disabled={saving}
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : saved ? (
                      <Check size={16} />
                    ) : null
                  }
                >
                  {saved ? 'Saved' : 'Save profile'}
                </SecondaryButton>
              }
            />

            {errorMsg && (
              <Text fontSize={FONT.sm} color="#dc2626">
                {errorMsg}
              </Text>
            )}
          </>
        )}
      </Box>
    );
  }
);

ArtistProfileCard.displayName = 'ArtistProfileCard';

export default ArtistProfileCard;
